import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export interface AdminSectionDef {
  key: string;
  label: string;
  icon_name: string;
  group_name: string;
  sort_order: number;
  is_active: boolean;
  superadmin_only: boolean;
  description: string | null;
}

/** @deprecated Use string directly. Kept for backwards compat. */
export type AdminSection = string;

interface UseAdminSectionsReturn {
  /** Section keys the current user has access to */
  sections: string[];
  /** Full section definitions from admin_sections table */
  sectionDefs: AdminSectionDef[];
  hasSection: (section: string) => boolean;
  hasAnyPermission: boolean;
  loading: boolean;
  isSuperAdmin: boolean;
}

/**
 * Hook to get the admin sections a user has access to.
 * Uses React Query for caching — multiple components calling this hook
 * share the same cache, preventing duplicate requests.
 */
export const useAdminSections = (): UseAdminSectionsReturn => {
  const { user, loading: authLoading } = useAuth();

  const isSuperAdmin = user?.email === 'sergio@reamanagers.com';

  // Fetch section definitions (shared across all callers)
  const { data: sectionDefs = [], isLoading: isLoadingDefs } = useQuery({
    queryKey: ['admin-section-defs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_sections')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      return (data || []) as AdminSectionDef[];
    },
    enabled: !!user,
    staleTime: 5 * 60_000, // 5 min — section defs rarely change
  });

  // Fetch user's permissions (shared across all callers for same user)
  const { data: sections = [], isLoading: isLoadingSections } = useQuery({
    queryKey: ['admin-user-sections', user?.id],
    queryFn: async () => {
      if (!user) return [];

      // SuperAdmin gets all sections
      if (isSuperAdmin) {
        const { data, error } = await supabase
          .from('admin_sections')
          .select('key')
          .eq('is_active', true)
          .order('sort_order');
        if (error) throw error;
        return (data || []).map((s: { key: string }) => s.key) as string[];
      }

      // Other users: call the DB function
      const { data, error } = await supabase.rpc('get_admin_sections', {
        _user_id: user.id
      });

      if (error) throw error;
      return (data || []) as string[];
    },
    enabled: !!user && !authLoading,
    staleTime: 5 * 60_000, // 5 min
  });

  const loading = authLoading || isLoadingDefs || isLoadingSections;

  const hasSection = (section: string): boolean => {
    if (isSuperAdmin) return true;
    return sections.includes(section);
  };

  const hasAnyPermission = isSuperAdmin || sections.length > 0;

  return {
    sections,
    sectionDefs,
    hasSection,
    hasAnyPermission,
    loading,
    isSuperAdmin
  };
};
